import {Event} from '../../constants/Event'

/**
 * @description
 * Common trait used for Nodes which needs image handling.
 * ### Usage
 * Extending class's schema requires the following property definitions in its `schema`:
 * ```
 * imageFile: {type: 'file', optional: true},
 * caption: {type: 'string', default: ''}
 * ```
 *
 * @memberof Trait
 * @typedef Trait.imageNodeTrait
 * @mixin imageNodeTrait
 *
 * @example
 * import {withTraits, traitBundle} from 'writer'
 * import {BlockNode} from 'substance'
 * const {imageNodeTrait} = traitBundle
 *
 * class MyImageNode extends withTraits(BlockNode, imageNodeTrait) {
 *    // Class MyImageNode will contain all functions from imageNodeTrait
 * }
 *
 * MyImageNode.define({
 *    type: 'myimage',
 *    imageFile: {type: 'file', optional: true},
 *    caption: {type: 'string', default: ''}
 * })
 *
 * @property {(function(Array.<string>): Promise)} fetchSpecifiedUrls
 * @property {(function())} getImageFile
 * @property {(function())} getUrl
 * @property {(function())} getOriginalUrl
 * @property {(function())} downloadOriginalImage
 * @property {(function())} getName
 */
const imageNodeTrait = (api) => {

    return {
        constructor() {
            if (typeof this.onImageUploaded === 'function') {
                this.on('onImageUploaded', () => this.onImageUploaded())
            }
        },

        /**
         * @memberOf Trait.imageNodeTrait
         * @returns {DocumentNode|undefined} a Node instance, or undefined if not found.
         */
        getImageFile() {
            if (this.imageFile) {
                return this.document.get(this.imageFile)
            }
        },

        /**
         * @memberOf Trait.imageNodeTrait
         * @param {Array.<string>} fallbacks array of service names of fallback services
         * @returns {Promise.<string>}
         */
        fetchSpecifiedUrls(fallbacks) {
            let imageFile = this.getImageFile()
            if (!imageFile) {
                return Promise.reject('No image file available')
            }

            if (!imageFile.proxy) {
                return Promise.reject('No image file available')
            }

            return imageFile.proxy.fetchSpecifiedUrls(fallbacks)
        },


        getServiceUrl(params) {
            let imageFile = this.getImageFile()

            if (!imageFile && !imageFile.proxy) {
                return Promise.reject('No image file available')
            }

            return imageFile.proxy.fetchSpecifiedUrls('service', params)
        },

        /**
         * Get the (signed) url to the original image (S3).
         * @memberOf Trait.imageNodeTrait
         * @param params
         * @return {Promise.<string>}
         */
        getOriginalUrl(params) {
            let imageFile = this.getImageFile()

            if (!imageFile && !imageFile.proxy) {
                return Promise.reject('No image file available')
            }

            return imageFile.proxy.fetchSpecifiedUrls(['original'], params)
        },

        /**
         * @memberOf Trait.imageNodeTrait
         */
        getUrl() {
            let imageFile = this.getImageFile()
            if (imageFile) {
                return imageFile.getUrl()
            }
        },

        /**
         * @memberOf Trait.imageNodeTrait
         */
        getName() {
            let imageFile = this.getImageFile()
            let name = null

            if (imageFile && imageFile.uri) {
                const index = imageFile.uri.lastIndexOf('/')
                name = index > 0 ? imageFile.uri.substring(index + 1) : null
            }

            if (name === null) {
                // No uri supplied using generic file name for download
                name = 'unknown'
            }

            return name
        },

        /**
         * Download copy of original image
         * @memberOf Trait.imageNodeTrait
         */
        downloadOriginalImage() {
            // Get file name from uri
            const fileName = this.getName()

            // Disable warning of "unsaved changes" in writer while
            // downloading image
            api.events.trigger(null, Event.DISABLE_UNLOAD_WARNING, {})

            // Get download (signed) url to original image
            // (no parameters needed).
            this.getOriginalUrl(null)
                .then((rawUrl) => {
                    // Encode url to be able to use it as a query param
                    const queryUrl = encodeURIComponent(rawUrl)

                    // Trigger download using image proxy
                    // and extracted file name
                    window.location = `/api/imageproxy?url=${queryUrl}&filename=${fileName}`

                    // Enable warning of "unsaved changes" in writer
                    // after download has been initialized
                    setTimeout(() => {
                        api.events.trigger(null, Event.ENABLE_UNLOAD_WARNING, {})
                    }, 1)

                })
                .catch((e) => {
                    console.warn(e)

                    // Enable warning of "unsaved changes" in writer
                    // after download has been initialized
                    api.events.trigger(null, Event.ENABLE_UNLOAD_WARNING, {})
                })

        }
    }

}

export {imageNodeTrait}
